import Notification from "../../../classes/Notification";
import { db, messaging, vapidKey } from "../../../boot/firebase";
import {
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { getToken } from "firebase/messaging";

const MAX_DEVICES = 3;
const HOUR = 36e5;

export const INIT_PUSH_NOTIFICATIONS = async ({ state }) => {
  if (!messaging) {
    return;
  }
  if (!state.user || !state.user.id) {
    throw "User not authorized";
  }
  const uid = state.user.id;

  function sendTokenToServer(token) {
    let tokens;
    if (state.user.tokens) {
      tokens = [...state.user.tokens];
      let index = tokens.indexOf(token);
      if (index >= 0) {
        tokens.splice(index, 1);
      }
      tokens.unshift(token);
      if (tokens.length > MAX_DEVICES) {
        tokens.length = MAX_DEVICES;
      }
    } else {
      tokens = [token];
    }
    return updateDoc(doc(db, `users/${uid}`), { tokens, modifiedBy: uid });
  }

  try {
    // Use the modular `getToken` function: `getToken(messagingInstance, { options })`
    const currentToken = await getToken(messaging, { vapidKey });

    if (currentToken) {
      console.log("FCM registration token obtained:", currentToken);
      // Send this token to your backend. This single call handles both
      // the initial token acquisition and any implicit refreshes that
      // `getToken` might have performed.
      return sendTokenToServer(currentToken);
    } else {
      // Permission hasn't been granted, or there's an environment issue
      console.log(
        "No FCM registration token available. User may need to grant notification permission or there's an issue."
      );
      throw new Error(
        "FCM token could not be retrieved. User permission or environmental issue."
      );
    }
  } catch (error) {
    console.warn(
      "An error occurred while retrieving or sending FCM token:",
      error
    );
  }
};

async function getNotificationCountsFromServer(uid) {
  const notificationsRef = collection(
    db,
    `users/${uid}/notifications`
  ).withConverter(Notification);
  const [totalSnap, undoneSnap, unreadSnap] = await Promise.all([
    getCountFromServer(notificationsRef),
    getCountFromServer(query(notificationsRef, where("done", "==", false))),
    getCountFromServer(
      query(
        notificationsRef,
        where("done", "==", false),
        where("seen", "==", false)
      )
    ),
  ]);
  return {
    total: totalSnap.data().count,
    undone: undoneSnap.data().count,
    unread: unreadSnap.data().count,
  };
}

export const LISTEN_NOTIFICATION_COUNTS = async ({
  commit,
  dispatch,
  state,
}) => {
  if (!state.user || !state.user.id) {
    throw "Not logged in";
  }
  const uid = state.user.id;
  const path = `users/${uid}/counters/notifications`;
  dispatch("UNLISTEN", path);
  commit("CLEAR_NOTIFICATION_COUNTS");

  // If the counters doc is missing (e.g., first login after this change),
  // compute the counts once server-side so the badge is correct until a
  // Cloud Function write creates the doc.
  const counterSnap = await getDoc(doc(db, path));
  let counts = counterSnap.exists()
    ? counterSnap.data()
    : await getNotificationCountsFromServer(uid).catch((error) => {
        console.error("Error counting notifications:", error);
        return { total: 0, undone: 0, unread: 0 };
      });
  commit("SET_NOTIFICATION_COUNTS", counts);

  const unsubscribe = onSnapshot(doc(db, path), (snapshot) => {
    if (snapshot.exists()) {
      commit("SET_NOTIFICATION_COUNTS", snapshot.data());
    }
  });
  commit("LISTEN", { path, unsubscribe });
  return path;
};

export const LISTEN_NOTIFICATIONS = (
  { commit, dispatch, state },
  { done = false, limitCount } = {}
) => {
  if (!state.user || !state.user.id) {
    throw "Not logged in";
  }
  const path = Notification.getPath();
  dispatch("UNLISTEN", path);
  commit("CLEAR_NOTIFICATIONS");
  const constraints = [orderBy("timestamp", "desc")];
  if (done !== null) {
    constraints.push(where("done", "==", done));
  }
  if (limitCount) {
    constraints.push(limit(limitCount));
  }
  const unsubscribe = onSnapshot(
    query(collection(db, path).withConverter(Notification), ...constraints),
    (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "removed") {
          commit("REMOVE_NOTIFICATION", change.oldIndex);
        } else {
          commit("SET_NOTIFICATION", {
            notification: change.doc.data(),
            oldIndex: change.oldIndex,
            newIndex: change.newIndex,
          });
        }
      });
    }
  );
  commit("LISTEN", { path, unsubscribe });
  return path;
};

function notificationsPageQuery(startAfterDoc, limitCount) {
  const constraints = [orderBy("timestamp", "desc"), limit(limitCount)];
  if (startAfterDoc) {
    constraints.push(startAfter(startAfterDoc));
  }
  return query(
    collection(db, Notification.getPath()).withConverter(Notification),
    ...constraints
  );
}

const toNotificationsPage = (snapshot) => ({
  docs: snapshot.docs.map((d) => d.data()),
  snapshots: snapshot.docs,
  lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
});

export const GET_NOTIFICATIONS_PAGE = async (
  { state },
  { startAfterDoc = null, limitCount = 20 } = {}
) => {
  if (!state.user || !state.user.id) {
    throw "Not logged in";
  }
  return toNotificationsPage(
    await getDocs(notificationsPageQuery(startAfterDoc, limitCount))
  );
};

export const LISTEN_NOTIFICATIONS_PAGE = (
  { commit, dispatch, state },
  {
    listenerPath: path = "notifications-page",
    startAfterDoc = null,
    limitCount = 20,
    next,
    error,
  }
) => {
  if (!state.user || !state.user.id) {
    throw "Not logged in";
  }
  dispatch("UNLISTEN", path);
  const unsubscribe = onSnapshot(
    notificationsPageQuery(startAfterDoc, limitCount),
    (snapshot) => next(toNotificationsPage(snapshot)),
    error
  );
  commit("LISTEN", { path, unsubscribe });
  return path;
};

export const NOTIFICATION_SEEN = ({ state }, { id, seen }) => {
  if (!state.user || !state.user.id) {
    throw "Not logged in";
  }
  const path = Notification.getPath(id);
  const data = { seen };
  if (!seen) {
    data.done = false;
  }
  return updateDoc(doc(db, path), data);
};

export const NOTIFICATION_DONE = ({ state }, { id, done }) => {
  if (!state.user || !state.user.id) {
    throw "Not logged in";
  }
  const path = Notification.getPath(id);
  const data = { done };
  if (done) {
    data.seen = true;
  }
  return updateDoc(doc(db, path), data);
};

export const NOTIFICATION_SNOOZE = async (
  { state },
  { id, hours, timestamp }
) => {
  if (!state.user || !state.user.id) {
    throw "Not logged in";
  }

  const notificationRef = doc(db, Notification.getPath(id));
  const snapshot = await getDoc(notificationRef);
  if (!snapshot.exists()) {
    throw "Not found";
  }
  const reminder = snapshot.data();

  reminder.seen = false;
  reminder.done = false;
  reminder.timestamp = timestamp || Date.now() + hours * HOUR;

  await setDoc(doc(db, Notification.getPath(id, true)), reminder);
  deleteDoc(notificationRef);
};

// Reminders

export const LISTEN_REMINDERS = ({ commit, dispatch, state }) => {
  if (!state.user || !state.user.id) {
    throw "Not logged in";
  }
  const path = Notification.getPath(null, true);
  dispatch("UNLISTEN", path);
  commit("CLEAR_REMINDERS");
  const unsubscribe = onSnapshot(
    query(
      collection(db, path).withConverter(Notification),
      orderBy("timestamp", "asc")
    ),
    (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "removed") {
          commit("REMOVE_REMINDER", change.oldIndex);
        } else {
          commit("SET_REMINDER", {
            reminder: change.doc.data(),
            oldIndex: change.oldIndex,
            newIndex: change.newIndex,
          });
        }
      });
    }
  );
  commit("LISTEN", { path, unsubscribe });
  return path;
};

export const CANCEL_REMINDER = async ({ state }, id) => {
  if (!state.user || !state.user.id) {
    throw "Not logged in";
  }

  const reminderRef = doc(db, Notification.getPath(id, true));
  const snapshot = await getDoc(reminderRef);
  if (!snapshot.exists()) {
    throw "Not found";
  }
  const notification = snapshot.data();
  notification.seen = true;
  notification.done = true;
  // The server pushes and emails every notification written here unless it is
  // `done` with `emailSent` set, and it checks that before the user's
  // preferences, which would override `sendPush`/`sendEmail`. A cancelled
  // reminder must go out neither way.
  notification.emailSent = true;
  notification.timestamp = Date.now();

  await setDoc(doc(db, Notification.getPath(id)), notification);
  deleteDoc(reminderRef);
};

export const RESCHEDULE_REMINDER = ({ state }, { id, timestamp }) => {
  if (!state.user || !state.user.id) {
    throw "Not logged in";
  }
  const path = Notification.getPath(id, true);
  return updateDoc(doc(db, path), { timestamp });
};
